import { z } from 'zod';
import { optionalEmail, optionalPhone, optionalString, requiredString } from './common';
import { logoDataUrlSchema } from './signature';
import { presentValues } from '../names';

/**
 * The lab's own identity.
 *
 * One row, so there is no create and no delete — only `GET /lab-profile` and
 * `PATCH /lab-profile`. Every field except the name is optional: a lab that has
 * not filled in its NTN should not be blocked from correcting its phone number.
 */
export const updateLabProfileSchema = z.object({
  name: requiredString('Lab name'),
  tagline: optionalString(),
  address_line1: optionalString(),
  address_line2: optionalString(),
  city: optionalString(),
  phone_primary: optionalPhone(),
  phone_secondary: optionalPhone(),
  email: optionalEmail(),
  website: optionalString(),
  license_no: optionalString(),
  ntn_no: optionalString(),
  logo_data_url: logoDataUrlSchema,
});
export type UpdateLabProfileInput = z.infer<typeof updateLabProfileSchema>;

/**
 * The shape the letterhead composers below need.
 *
 * Structural rather than the wire type, so `server/print/` can compose lines
 * from a Prisma row without a mapping step, and so these two functions stay
 * usable from a test with an object literal.
 */
export interface LabIdentity {
  name: string;
  tagline?: string | null | undefined;
  address_line1?: string | null | undefined;
  address_line2?: string | null | undefined;
  city?: string | null | undefined;
  phone_primary?: string | null | undefined;
  phone_secondary?: string | null | undefined;
  email?: string | null | undefined;
  website?: string | null | undefined;
  license_no?: string | null | undefined;
  ntn_no?: string | null | undefined;
}

/**
 * The lines that sit under the lab's name on a letterhead.
 *
 * Composed from the structured fields rather than being a second free-text
 * block the operator has to keep in step with them. The address collapses onto
 * one line because a letterhead band is measured in millimetres and every line
 * costs one — see `minimumHeaderBand`.
 */
export function letterheadLines(profile: LabIdentity): string[] {
  const address = presentValues(profile.address_line1, profile.address_line2, profile.city).join(
    ', ',
  );
  const phones = presentValues(profile.phone_primary, profile.phone_secondary).join(', ');

  return presentValues(profile.tagline, address || null, phones || null, profile.email);
}

/**
 * The contact block for a footer column.
 *
 * Phones, email, address — the order the lab's existing sheet already prints
 * them in, and deliberately not the same order as `letterheadLines`, where the
 * address leads because the phone numbers are the least useful thing at the top
 * of a page.
 */
export function contactLines(profile: LabIdentity): string[] {
  const address = presentValues(profile.address_line1, profile.address_line2, profile.city).join(
    ', ',
  );
  const phones = presentValues(profile.phone_primary, profile.phone_secondary).join(', ');

  return presentValues(phones || null, profile.email, address || null, profile.website);
}

/** "Licence 1234 · NTN 5678" — the regulatory footer of an official document. */
export function registrationLine(profile: LabIdentity): string {
  return presentValues(
    profile.license_no ? `Licence ${profile.license_no.trim()}` : null,
    profile.ntn_no ? `NTN ${profile.ntn_no.trim()}` : null,
  ).join(' · ');
}
